/**
 * Submits one file to a presigned POST form, straight from the browser.
 *
 * Shared by every surface that uploads through a presign — the app chat here and
 * the platform's own chat — because the two rules that make it work are easy to
 * get subtly wrong and are worth stating once: the file field must come LAST,
 * and the request must carry NO bearer token. The destination is a third-party
 * object store whose form authenticates itself, so attaching ours would leak it
 * to a host that has no business holding it. The bytes never travel through our
 * API, which is the point — a ten-megabyte body would otherwise share the SSE
 * request path with the turn.
 *
 * `XMLHttpRequest` rather than `fetch`: it is still the only browser API that
 * reports UPLOAD progress, and a ten-megabyte attachment whose chip cannot say
 * how far along it is looks hung.
 */ export function uploadToPresignedPost(url, fields, file, options) {
    const form = new FormData();
    for (const [name, value] of Object.entries(fields)){
        form.append(name, value);
    }
    // The file goes LAST: S3 ignores every field that follows it.
    form.append("file", file);
    return new Promise((resolve, reject)=>{
        const request = new XMLHttpRequest();
        request.open("POST", url);
        const abort = ()=>request.abort();
        options?.signal?.addEventListener("abort", abort);
        const settle = (finish)=>{
            options?.signal?.removeEventListener("abort", abort);
            finish();
        };
        request.upload.onprogress = (event)=>{
            if (event.lengthComputable && event.total > 0) {
                options?.onProgress?.(Math.round(event.loaded / event.total * 100));
            }
        };
        request.onload = ()=>settle(()=>request.status >= 200 && request.status < 300 ? resolve() : reject(new Error(`Upload failed: ${request.status} ${request.statusText}`)));
        request.onerror = ()=>settle(()=>reject(new Error("Upload failed")));
        request.onabort = ()=>settle(()=>reject(new DOMException("Upload aborted", "AbortError")));
        // A draft removed while its presign was still in flight aborts a signal that
        // is already aborted by the time we get here, and `addEventListener` never
        // fires for that — without this check the bytes would upload anyway.
        if (options?.signal?.aborted) {
            return settle(()=>reject(new DOMException("Upload aborted", "AbortError")));
        }
        request.send(form);
    });
}

//# sourceMappingURL=presigned-upload.js.map